import { useEffect, useState } from 'react'

type Props = {
  html: string
  language?: string
  clipboard?: boolean
  code?: string
}

export default function CodeBlock({ html, language, clipboard = false, code }: Props) {
  return (
    <div className='code-block' data-clipboard={clipboard || undefined} data-language={language}>
      {clipboard && code != null && <CopyButton code={code} />}
      <div className='max-w-full min-w-0' dangerouslySetInnerHTML={{ __html: html }} />
    </div>
  )
}

function CopyButton({ code }: { code: string }) {
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!copied) {
      return
    }

    const timeout = setTimeout(() => setCopied(false), 2000)
    return () => clearTimeout(timeout)
  }, [copied])

  async function copy() {
    if (window.isSecureContext && navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(code)
        setCopied(true)
        return
      } catch {
        // Fall through to execCommand on http://pscaledev.com and similar
      }
    }

    if (copyText(code)) {
      setCopied(true)
    }
  }

  return (
    <button
      aria-label={copied ? 'Copied' : 'Copy code'}
      className='absolute top-1 right-1 z-10 border bg-gray-100 p-1 text-primary hover:text-blue dark:bg-gray-850'
      onClick={copy}
      type='button'
    >
      {copied ? <CheckIcon /> : <ClipboardIcon />}
      <span aria-live='polite' className='sr-only'>
        {copied ? 'Copied' : ''}
      </span>
    </button>
  )
}

function copyText(text: string) {
  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.setAttribute('readonly', '')
  textarea.style.position = 'fixed'
  textarea.style.top = '0'
  textarea.style.left = '0'
  textarea.style.opacity = '0'

  document.body.appendChild(textarea)
  textarea.focus()
  textarea.select()
  textarea.setSelectionRange(0, textarea.value.length)

  try {
    return document.execCommand('copy')
  } catch {
    return false
  } finally {
    document.body.removeChild(textarea)
  }
}

function ClipboardIcon() {
  return (
    <svg fill='none' height='16' viewBox='0 0 16 16' width='16' xmlns='http://www.w3.org/2000/svg'>
      <rect height='9' stroke='currentColor' width='9' x='5.5' y='5.5' />
      <path d='M10.5 3.5H3.5V10.5' stroke='currentColor' />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg fill='none' height='16' viewBox='0 0 16 16' width='16' xmlns='http://www.w3.org/2000/svg'>
      <path d='M3.5 8.5L6.5 11.5L12.5 4.5' stroke='currentColor' />
    </svg>
  )
}
